import { IconNotification } from "@light-technology/components";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

type UseMutationWithToastProps = {
  successMessage?: string | (() => string);
  errorMessage?: string;
  onSuccess?: () => void;
};

type MutationState = {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
};

export const useMutationWithToast = <T, U extends MutationState>(
  mutationResult: readonly [T, U],
  { successMessage, errorMessage, onSuccess }: UseMutationWithToastProps,
): [T, U] => {
  const [mutation, mutationState] = mutationResult;
  const hasHandledSuccess = useRef(false);
  const hasHandledError = useRef(false);
  const onSuccessRef = useRef(onSuccess);

  // Keep the latest onSuccess callback in the ref
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  // Reset flags when mutation starts
  useEffect(() => {
    if (mutationState.isLoading) {
      hasHandledSuccess.current = false;
      hasHandledError.current = false;
    }
  }, [mutationState.isLoading]);

  useEffect(() => {
    if (mutationState.isSuccess && !hasHandledSuccess.current) {
      hasHandledSuccess.current = true;
      if (successMessage) {
        const message =
          typeof successMessage === "function"
            ? successMessage()
            : successMessage;
        toast.custom(
          (t) => (
            <IconNotification
              color="success"
              description={message}
              hideDismissLabel
              onClose={() => toast.dismiss(t)}
            />
          ),
          { duration: 3000 },
        );
      }
      onSuccessRef.current?.();
    }
  }, [mutationState.isSuccess, successMessage]);

  useEffect(() => {
    if (mutationState.isError && !hasHandledError.current) {
      hasHandledError.current = true;
      toast.custom(
        (t) => (
          <IconNotification
            color="error"
            description={
              errorMessage ?? "Something went wrong. Please try again later."
            }
            hideDismissLabel
            onClose={() => toast.dismiss(t)}
          />
        ),
        { duration: 5000 },
      );
    }
  }, [mutationState.isError, errorMessage]);

  return [mutation, mutationState];
};
